<template>
  <div
    class="machine-backups-table"
    style="height: 100%;">

    <el-divider v-if="!tableIsNotEmpty"/>
    <div
      v-if="!tableIsNotEmpty"
      class="boxes-table-no-data-container">
      <div class="boxes-table-no-data-container-body">
        <div class="boxes-table-no-data-image" />
        <div class="mt-24 el-text el-text--display-regular">
          {{ $t('wr-dashboard.backups-table.message.no-backups') }}
        </div>
      </div>
    </div>

    <el-table
      row-key="id"
      v-if="tableIsNotEmpty"
      style="height: 100%;"
      height="fit-scrollable"
      :data="getBackups"
      highlight-current-row
      :default-sort = "{ prop: 'displayName' }"
      :loading-append="isFetchMachineBackupsInProgress"
      @row-click="onTableRowClick"
      @row-dblclick="onTableRowDblClick"
      ref="table"
      border-bottom>

      <el-table-column
        prop="id"
        label="id"
        hidden/>

      <el-table-column
        prop="displayName"
        :label="$t('wr-dashboard.backups-table.column.name')"
        align="left"
        sortable>
        <template slot-scope="{ row }">
          <el-icon
            :style="!row.options.browsable ? 'opacity: 0.35' : ''"
            :name="'stub' | archiveIcon(row.options.notarized ? 'NOTARIZED' : row.type)"/>
          <span class="ml-16">
            {{ row.displayName }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="extension"
        label="extension"
        hidden/>

      <el-table-column
        prop="status"
        align="right"
        width="128"
        label="">
        <template slot-scope="{ row }">
          <div
            v-if="row.options.notarized"
            class="mr-16">
            <el-icon
              slot="icon"
              color="brand-light"
              name="i-license--16"/>
          </div>
          <div
            v-if="row.options.passwordRequired"
            class="mr-16">
            <el-icon
              slot="icon"
              color="brand-light"
              name="i-internet-access--16"/>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        prop="size"
        align="right"
        width="180"
        :label="$t('wr-dashboard.backups-table.column.size')"
        :sort-by="row => row.size"
        sortable>
        <template slot-scope="{ row }">
          {{ row.size | size }}
        </template>
      </el-table-column>

      <el-table-column
        prop="lastVersion"
        align="left"
        width="280"
        :label="$t('wr-dashboard.backups-table.column.version')"
        :sort-by="row => new Date(row.lastVersion).getTime()"
        sortable>
        <template slot-scope="{ row }">
          {{ row.lastVersion | date }}
        </template>
      </el-table-column>
    </el-table>

    <password-dialog-box
      v-if="passwordDialogBoxVisible"
      :archive-id="selectedBox.id"
      :box-group="selectedBox.group"
      :archive-display-name="selectedBox.displayName"
      :payload="passwordDialogBoxPayload"
      @handleSubmit="handlePasswordSubmit"
      @handleClose="handlePasswordClose"
    />

    <delete-archive-dialog-box
      v-if="deleteDialogBoxVisible"
      :archive-id="selectedBox.id"
      :box-group="selectedBox.group"
      @handleClose="handleDeleteClose"
    />

    <el-sidebar-layout
      class="el-sidebar"
      v-if="isSideBarOpen"
      open
      @close="sidebarClose()">

      <template #sidebar>
        <el-sidebar-panel
          @command="handleSidebarCommand"
          :title="sidebarTitle"
          show-close
          :actions="sidebarActions"
          :properties="sidebarProps">
          <template
            #preview
            v-if="isSideBarPreviewEnabled">
            <div class="el-sidebar-preview">
              <el-icon
                size="128"
                class="el-sidebar-icon"
                :name="sidebarPreviewIconName"/>
            </div>
          </template>
        </el-sidebar-panel>
      </template>
    </el-sidebar-layout>


  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import PasswordDialogBox from './PasswordDialogBox';
import DeleteArchiveDialogBox from './DeleteArchiveDialogBox';

import {
  COMPLETED_STATUS,
  PENDING_STATUS
} from '../store/action-status';

import backupType from '../store/backup-types';

import {
  FETCH_MACHINE_BACKUPS,
  UPDATE_ACTIVE_BACKUP
} from '../store/action-types';

const { mapGetters, mapActions } = createNamespacedHelpers('wr-dashboard');

export default {
  name: 'MachineBackupsTable',
  components: {
    PasswordDialogBox,
    DeleteArchiveDialogBox
  },
  data() {
    return {
      selectedBox: {},
      passwordDialogBoxPayload: {},
      passwordDialogBoxVisible: false,
      deleteDialogBoxVisible: false,
      selectedItems: []
    };
  },
  computed: {
    ...mapGetters([
      'getBackups',
      'getActiveMachineId',
      'getFetchBackupsStatus',
      'getActiveContentType',
      'getArchiveDeletionStatus',
      'getPostLoginArchiveObId',
      'getPostLoginMachineName'
    ]),
    isFetchMachineBackupsInProgress() {
      return this.getFetchBackupsStatus === PENDING_STATUS;
    },
    isSideBarOpen() {
      return this.selectedItems.length > 0;
    },
    isSideBarPreviewEnabled() {
      return this.selectedItems.length === 1;
    },
    sidebarPreviewIconName() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.$options.filters.archiveIcon('stub', row.options.notarized ? 'NOTARIZED' : row.type);
      }
      return '';
    },
    sidebarTitle() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.getSidebarBackupNameField(row.displayName, row.extension);
      }
      return this.$tc(
        'wr-dashboard.side-bar.selected.title:plural',
        this.selectedItems.length, { count: this.selectedItems.length }
      );
    },
    sidebarProps() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return [
          {
            label: this.$t('wr-dashboard.backups-sidebar.label.size'),
            value: this.$options.filters.size(row.size)
          },
          {
            label: this.$t('wr-dashboard.backups-sidebar.label.type'),
            value: this.getBackupTypeText(row.type, row.options.notarized)
          },
          {
            label: this.$t('wr-dashboard.backups-sidebar.label.created'),
            value: this.$options.filters.date(row.created)
          },
          {
            label: this.$t('wr-dashboard.backups-sidebar.label.machine'),
            value: row.machineName
          },
          {
            label: this.$t('wr-dashboard.backups-sidebar.label.owner'),
            value: row.ownerName
          }
        ];
      }
      return [];
    },
    sidebarActions() {
      const actions = [];
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.getMenuActions(row);
      }
      return actions;
    },
    tableIsNotEmpty() {
      return this.isFetchMachineBackupsInProgress || this.getBackups.length > 0;
    }
  },
  watch: {
    getFetchBackupsStatus(status) {
      if (status === PENDING_STATUS) {
        this.sidebarClose();
      }
    },
    getActiveContentType(itemId) {
      this.sidebarClose();
    },
    getArchiveDeletionStatus(status) {
      if (status === COMPLETED_STATUS && this.getBackups.length > 0) {
        this.loadActiveMachineBackups();
      }
    },
    getPostLoginArchiveObId(archiveObjId) {
      if (this.getPostLoginMachineName !== '' && archiveObjId !== '') {
        this.openBox({
          id: archiveObjId
        });
      }
    }
  },
  methods: {
    ...mapActions({
      fetchMachineBackups: FETCH_MACHINE_BACKUPS,
      updateActiveBackup: UPDATE_ACTIVE_BACKUP
    }),
    getSidebarBackupNameField(displayName, extension) {
      if (displayName.toLowerCase().indexOf(extension.toLowerCase()) !== -1) {
        return displayName;
      }
      return displayName + extension;
    },
    getBackupTypeText(type, isNotarized) {
      return this.$t(backupType(type, isNotarized).i18nKey);
    },
    sidebarClose() {
      // this.$refs.table.clearSelection();
      this.selectedItems = [];
    },
    getMenuActions(row) {
      const actions = [];

      if (row.options.browsable) {
        actions.push({
          label: this.$t('wr-dashboard.form.button.open'),
          command: 'open',
          icon: 'i-long-arrow-right--16'
        });
      }

      if (row.options.deletionEnabled) {
        actions.push({
          label: this.$t('wr-dashboard.backups-table.action.delete'),
          command: 'delete',
          icon: 'i-trash-o--16'
        });
      }
      return actions;
    },
    handleSidebarCommand(command) {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        this.handleAction(command, row);
      } else {
        console.log('group operation');
      }
      this.sidebarClose();
    },
    handleAction(command, tableRow) {
      if (command === 'delete') {
        this.selectedBox = this.getSelectedBox(tableRow.id);
        this.showDeleteArchiveDialogBox(true);
      }
      if (command === 'open') {
        this.openBox(tableRow);
      }
    },
    onTableRowClick(row, event, column) {
      if (column.prop === 'displayName') {
        this.openBox(row);
      } else {
        this.$refs.table.clearSelection();
        this.selectedItems = [row];
      }
    },
    onTableRowDblClick(row) {
      this.openBox(row);
    },
    openBox(row) {
      // this.$refs.table.clearSelection();
      // this.$refs.table.$emit('selection-change', []);
      this.sidebarClose();
      if (this.isBrowsable(row.id)) {
        if (this.isPasswordRequired(row.id, 'open')) {
          this.showPasswordDialogBox(true);
        } else {
          this.emitBoxSelectedEvent(row.id);
        }
      }
    },
    isPasswordRequired(boxId, action) {
      this.selectedBox = this.getSelectedBox(boxId);
      this.passwordDialogBoxPayload = { command: action };
      return this.selectedBox.options.passwordRequired === true;
    },
    isBrowsable(boxId) {
      const box = this.getSelectedBox(boxId);
      return box.options.browsable !== false;
    },
    getSelectedBox(boxId) {
      return this.getBackups.filter(obj => obj.id === boxId)[0];
    },
    showPasswordDialogBox(show) {
      this.passwordDialogBoxVisible = show;
    },
    handlePasswordSubmit(payload) {
      // console.log(payload.command);
      this.showPasswordDialogBox(false);
      if (payload.command === 'open') {
        this.emitBoxSelectedEvent(this.selectedBox.id);
      }
    },
    handlePasswordClose() {
      this.showPasswordDialogBox(false);
    },
    showDeleteArchiveDialogBox(show) {
      this.deleteDialogBoxVisible = show;
    },
    handleDeleteClose() {
      this.showDeleteArchiveDialogBox(false);
    },
    emitBoxSelectedEvent(id) {
      this.updateActiveBackup({
        activeBackupId: id,
        activeBackupDisplayName: this.getSelectedBox(id).displayName
      });

      this.$emit('boxSelected', {
        boxId: id
      });
    },
    loadActiveMachineBackups() {
      this.fetchMachineBackups({
        machineId: this.getActiveMachineId
      });
    }
  },
  mounted() {
    this.loadActiveMachineBackups();
  }
};
</script>

<style lang="scss" scoped>
  .boxes-table-no-data-container {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }

  .boxes-table-no-data-container-body {
    margin-left: 256px;
    margin-top: -78px;
  }

  .boxes-table-no-data-image {
    margin: auto;
    background: url('../assets/empty/empty-cloud.png') no-repeat;
    width: 128px;
    height: 128px;
  }
</style>
